import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Autocomplete,
  Stack,
  Paper,
  Checkbox,
  FormControlLabel,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Chip,
  Rating,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ReplayIcon from '@mui/icons-material/Replay';
import api from '../../services/api';
import {
  QUESTIONS_WITH_MISSING_ANSWERS,
  QUESTIONS_MISSING_ANSWERS_TOPIC_COUNTS,
  QUESTIONS_UPDATE_ANSWERS_WITH_AI,
  QUESTIONS,
  DETAIL_ANSWERS,
  AI_FEEDBACK_UI_ENABLED,
} from '../../config/Config';
import MathRenderer from '../common/MathRenderer';
import MathEditor from './MathEditor';
import { QuestionImage } from '../common/QuestionImage';
import { submitFeedback } from '../../api/feedback';
import { useGenerationTracking } from '../../hooks/useGenerationTracking';

const initialForm = {
  board: '',
  academic_year: '',
  subject: '',
  topic: '',
  maxQuestions: 5,
  updateScope: 'both',
  promptNote: '',
};

const FEEDBACK_TAGS = [
  { value: 'wrong_answer', label: 'Wrong answer' },
  { value: 'weak_steps', label: 'Weak steps' },
  { value: 'too_complex', label: 'Too complex' },
  { value: 'not_grade_aligned', label: 'Not grade aligned' },
  { value: 'did_not_follow_note', label: 'Did not follow note' },
];

const QUESTION_FEEDBACK_TAGS = [
  { value: 'answer_wrong', label: 'Answer issue' },
  { value: 'steps_unclear', label: 'Steps unclear' },
  { value: 'too_advanced', label: 'Too advanced' },
  { value: 'missing_concept', label: 'Missing concept' },
];

const getApiErrorMessage = (err, fallback) => {
  const status = err?.response?.status;
  const data = err?.response?.data;
  if (status === 429) {
    return 'AI rate limit reached. Please wait a bit before trying again.';
  }
  const stripHtml = (value) => String(value || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const detail =
    data?.error ||
    data?.detail ||
    data?.message ||
    (typeof data === 'string' ? data : '');
  const cleanDetail = stripHtml(detail);
  if (status >= 500 && (!cleanDetail || /internal server error/i.test(cleanDetail))) {
    return `${fallback} (${status}). The AI request failed on the server. Please try a smaller batch or try again in a moment.`;
  }
  return detail
    ? `${fallback}${status ? ` (${status})` : ''}: ${cleanDetail}`
    : `${fallback}${status ? ` (${status})` : ''}. Please try again.`;
};

/**
 * UpdateQuestionsWithAIDialog
 *
 * Finds questions for answer completion or instruction-driven text rewriting.
 * Teachers review original and proposed content before anything is saved.
 */
export default function UpdateQuestionsWithAIDialog({
  open,
  onClose,
  boards = [],
  academicYears = [],
  subjects = [],
  allTopics = [],
  difficulties = [],
  onSuccess,
  onError,
}) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [updates, setUpdates] = useState([]);
  const [selectedIndices, setSelectedIndices] = useState([]);
  const [topicMissingCounts, setTopicMissingCounts] = useState([]);
  const [topicsLoading, setTopicsLoading] = useState(false);
  const [originalUpdates, setOriginalUpdates] = useState([]);
  const [feedbackRating, setFeedbackRating] = useState(null);
  const [feedbackTags, setFeedbackTags] = useState([]);
  const [feedbackNote, setFeedbackNote] = useState('');
  const [questionFeedback, setQuestionFeedback] = useState({});
  const [feedbackSessionId, setFeedbackSessionId] = useState('');
  const [regeneratingQuestionIds, setRegeneratingQuestionIds] = useState([]);
  const [questionRegenerationNotes, setQuestionRegenerationNotes] = useState({});
  const { onGenerationStart, buildImplicitSignals, resetTracking } = useGenerationTracking();

  // Reset when dialog opens
  useEffect(() => {
    if (open) {
      setForm(initialForm);
      setLoading(false);
      setError('');
      setUpdates([]);
      setOriginalUpdates([]);
      setSelectedIndices([]);
      setTopicMissingCounts([]);
      setFeedbackRating(null);
      setFeedbackTags([]);
      setFeedbackNote('');
      setQuestionFeedback({});
      setFeedbackSessionId('');
      setRegeneratingQuestionIds([]);
      setQuestionRegenerationNotes({});
      resetTracking();
    }
  }, [open, resetTracking]);

  // Topics with missing-answer counts: only those that have at least one question needing answers
  const availableTopics = useMemo(() => {
    return (topicMissingCounts || []).map((r) => ({
      id: r.topic_id,
      topic: r.topic_name || '',
      question_count: r.count,
    }));
  }, [topicMissingCounts]);

  // Fetch per-topic counts when board, year, subject are set
  const fetchTopicMissingCounts = useCallback(async () => {
    if (!form.board || !form.academic_year || !form.subject) {
      setTopicMissingCounts([]);
      return;
    }
    setTopicsLoading(true);
    try {
      const res = await api.get(QUESTIONS_MISSING_ANSWERS_TOPIC_COUNTS, {
        params: {
          board: form.board,
          academic_year: form.academic_year,
          subject: form.subject,
          update_scope: form.updateScope,
        },
      });
      setTopicMissingCounts(Array.isArray(res.data) ? res.data : []);
      if (form.topic && !(res.data || []).some((r) => String(r.topic_id) === String(form.topic))) {
        setForm((prev) => ({ ...prev, topic: '' }));
      }
    } catch (err) {
      console.error('Failed to fetch topic counts for missing answers', err);
      setTopicMissingCounts([]);
    } finally {
      setTopicsLoading(false);
    }
  }, [form.board, form.academic_year, form.subject, form.topic, form.updateScope]);

  useEffect(() => {
    if (open && form.board && form.academic_year && form.subject) {
      fetchTopicMissingCounts();
    } else {
      setTopicMissingCounts([]);
    }
  }, [open, form.board, form.academic_year, form.subject, fetchTopicMissingCounts]);

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
    if (field === 'updateScope') {
      setUpdates([]);
      setSelectedIndices([]);
      setError('');
    }
  };

  const maxQuestionsOptions = useMemo(
    () => Array.from({ length: 6 }, (_, i) => 5 + i), // 5..10
    []
  );

  const isValid = () =>
    form.board && form.academic_year && form.subject && form.topic &&
    Number(form.maxQuestions) >= 5 && Number(form.maxQuestions) <= 10 &&
    (form.updateScope !== 'question' || Boolean(form.promptNote.trim()));

  const buildFeedbackContext = useCallback(() => {
    const subjectObj = subjects.find((s) => String(s.id) === String(form.subject));
    const topicObj = availableTopics.find((t) => String(t.id) === String(form.topic));
    const yearObj = academicYears.find((y) => String(y.id) === String(form.academic_year));
    return {
      module: 'answer_update',
      acad_year: yearObj?.label || yearObj?.year || '',
      subject: subjectObj?.name || '',
      topic: topicObj?.topic || topicObj?.name || '',
      grade_level: yearObj?.label || yearObj?.year || '',
    };
  }, [academicYears, availableTopics, form.academic_year, form.subject, form.topic, subjects]);

  const buildQuestionCharacteristics = useCallback((question, issueTags = []) => ({
    question_type: question?.question_type || question?.type || 'unknown',
    update_scope: form.updateScope,
    had_existing_answer: Boolean((question?.existingCorrect || '').trim()),
    had_existing_steps: Boolean((question?.existingDetail || '').trim()),
    requested_correct_answer: form.updateScope === 'both' || form.updateScope === 'correct',
    requested_detail_steps: form.updateScope === 'both' || form.updateScope === 'detail',
    requested_question_text: form.updateScope === 'question',
    issue_tags: issueTags,
    answer_length: String(question?.proposedCorrect || '').length,
    steps_length: String(question?.proposedDetail || '').length,
    question_text_length: String(question?.proposedQuestionText || '').length,
  }), [form.updateScope]);

  const mergeAIResultWithQuestion = useCallback((result, original = {}) => {
    const existingDetail = original.existingDetail ??
      ((original.detail_answers && original.detail_answers[0]?.answer) || '');
    const detailAnswerId = original.detailAnswerId ??
      ((original.detail_answers && original.detail_answers[0]?.id) || null);

    return {
      ...original,
      id: result.question_id,
      question_text: result.question_text || original.question_text,
      existingQuestionText: result.original_question_text || original.existingQuestionText || original.question_text || '',
      proposedQuestionText: result.proposed_question_text || original.proposedQuestionText || original.question_text || '',
      options: result.options || original.options || [],
      existingCorrect: original.existingCorrect ?? original.correct_answer ?? '',
      existingAnswerEvaluation: original.existingAnswerEvaluation ?? original.answer_evaluation ?? {},
      existingDetail,
      proposedCorrect: result.correct_answer || original.proposedCorrect || original.correct_answer || '',
      proposedAnswerEvaluation: result.answer_evaluation || original.proposedAnswerEvaluation || original.answer_evaluation || {},
      proposedDetail: result.detail_answer || original.proposedDetail || existingDetail,
      detailAnswerId,
      needsCorrectAnswer: Boolean(result.needs_correct_answer ?? original.needsCorrectAnswer),
      needsDetailAnswer: Boolean(result.needs_detail_answer ?? original.needsDetailAnswer),
      image_url: result.image_url ?? original.image_url ?? '',
      image_proxy_url: result.image_proxy_url ?? original.image_proxy_url ?? '',
    };
  }, []);

  const handleGenerate = async () => {
    if (!isValid()) {
      setError(
        form.updateScope === 'question' && !form.promptNote.trim()
          ? 'Enter an instruction describing how the question text should change.'
          : 'Please select Board, Class (Year), Subject, Topic and a max question count between 5 and 10.'
      );
      return;
    }

    setLoading(true);
    setError('');
    try {
      onGenerationStart();
      const sessionId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      setFeedbackSessionId(sessionId);
      const maxRequest = Math.min(20, Math.max(10, Number(form.maxQuestions) * 2));
      const listParams = {
        board: form.board,
        academic_year: form.academic_year,
        subject: form.subject,
        topic: form.topic,
        max: maxRequest,
        update_scope: form.updateScope,
      };
      let listRes;
      try {
        listRes = await api.get(QUESTIONS_WITH_MISSING_ANSWERS, {
          params: listParams,
        });
      } catch (err) {
        console.error('Update with AI - failed to load matching questions', {
          params: listParams,
          status: err?.response?.status,
          data: err?.response?.data,
          error: err,
        });
        throw new Error(getApiErrorMessage(err, 'Failed to load matching questions for AI update'));
      }
      const questions = listRes.data || [];
      const limited = questions.slice(0, Number(form.maxQuestions) || 5);

      if (!limited.length) {
        setError(
          form.updateScope === 'question'
            ? 'No questions found for the selected topic.'
            : 'No questions found with missing correct or detail answers for the selected topic.'
        );
        setUpdates([]);
        return;
      }

      const resultsPayload = [];
      const failedQuestionIds = [];

      for (const question of limited) {
        const aiPayload = {
          question_ids: [question.id],
          update_scope: form.updateScope,
          note_to_ai: form.promptNote.trim(),
          feedback_context: buildFeedbackContext(),
        };
        try {
          const aiRes = await api.post(QUESTIONS_UPDATE_ANSWERS_WITH_AI, aiPayload);
          const proposedResults = aiRes.data?.results || [];
          if (form.updateScope === 'question') {
            const validResults = proposedResults.filter((result) => (
              !result.error && String(result.proposed_question_text || '').trim()
            ));
            if (!validResults.length) failedQuestionIds.push(question.id);
            resultsPayload.push(...validResults);
          } else {
            resultsPayload.push(...proposedResults);
          }
        } catch (err) {
          failedQuestionIds.push(question.id);
          console.error('Update with AI - failed to generate answer proposal', {
            payload: { ...aiPayload, note_to_ai: aiPayload.note_to_ai ? '[present]' : '' },
            status: err?.response?.status,
            data: err?.response?.data,
            error: err,
          });
        }
      }

      if (!resultsPayload.length) {
        throw new Error(
          form.updateScope === 'question'
            ? 'AI could not propose updated question text. Please try a clearer instruction or try again in a moment.'
            : failedQuestionIds.length
              ? 'AI could not propose answers. The AI request failed on the server. Please try again in a moment.'
              : 'AI could not propose answers. No proposals were returned.'
        );
      }

      const byId = new Map(limited.map((q) => [q.id, q]));
      const merged = resultsPayload.map((r) => mergeAIResultWithQuestion(r, byId.get(r.question_id) || {}));

      setUpdates(merged);
      setOriginalUpdates(merged);
      setSelectedIndices(merged.map((_, i) => i));
      setQuestionFeedback({});
      setQuestionRegenerationNotes({});
      if (failedQuestionIds.length) {
        const proposalType = form.updateScope === 'question' ? 'updated text' : 'answers';
        setError(`AI skipped ${failedQuestionIds.length} question${failedQuestionIds.length > 1 ? 's' : ''} while proposing ${proposalType}. Review the proposals below, then try again for the skipped questions.`);
      }
    } catch (err) {
      console.error('Update with AI – fetch error', err);
      const msg = err?.message || 'Failed to fetch questions for AI update. Please try again.';
      setError(msg);
      onError?.(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateField = (id, field, value) => {
    setUpdates((prev) =>
      prev.map((u) =>
        u.id === id ? { ...u, [field]: value } : u
      )
    );
  };

  const handleToggle = (index) => {
    setSelectedIndices((prev) =>
      prev.includes(index)
        ? prev.filter((i) => i !== index)
        : [...prev, index]
    );
  };

  const setProposalAccepted = (index, accepted) => {
    setSelectedIndices((prev) => {
      if (accepted) return prev.includes(index) ? prev : [...prev, index];
      return prev.filter((item) => item !== index);
    });
  };

  const toggleFeedbackTag = (tag) => {
    setFeedbackTags((prev) =>
      prev.includes(tag)
        ? prev.filter((item) => item !== tag)
        : [...prev, tag]
    );
  };

  const toggleQuestionFeedbackTag = (questionId, tag) => {
    setQuestionFeedback((prev) => {
      const current = prev[questionId] || { tags: [], note: '' };
      const tags = current.tags.includes(tag)
        ? current.tags.filter((item) => item !== tag)
        : [...current.tags, tag];
      return {
        ...prev,
        [questionId]: { ...current, tags },
      };
    });
  };

  const updateQuestionFeedbackNote = (questionId, note) => {
    setQuestionFeedback((prev) => {
      const current = prev[questionId] || { tags: [], note: '' };
      return {
        ...prev,
        [questionId]: { ...current, note: note.slice(0, 500) },
      };
    });
  };

  const updateQuestionRegenerationNote = (questionId, note) => {
    setQuestionRegenerationNotes((prev) => ({
      ...prev,
      [questionId]: note.slice(0, 500),
    }));
  };

  const buildRegenerationInstruction = (question, note, tags) => {
    const parts = [];
    if (form.promptNote.trim()) {
      parts.push(`Original teacher instruction: ${form.promptNote.trim()}`);
    }
    if (tags.length) {
      parts.push(`Issues to fix for this question: ${tags.join(', ')}`);
    }
    if (note.trim()) {
      parts.push(`Regeneration instruction for this question: ${note.trim()}`);
    }
    if (form.updateScope === 'question') {
      parts.push('Rewrite only the question text. Keep the existing answer, numbers, and options valid.');
      if (question?.proposedQuestionText) {
        parts.push(`Previous proposed question text to improve: ${question.proposedQuestionText}`);
      }
      return parts.join('\n');
    }
    parts.push('Regenerate only the selected question answer/working. Keep the original question text unchanged.');
    if (question?.proposedCorrect) {
      parts.push(`Previous proposed answer to improve: ${question.proposedCorrect}`);
    }
    if (question?.proposedDetail) {
      parts.push(`Previous proposed working steps to improve: ${question.proposedDetail}`);
    }
    return parts.join('\n');
  };

  const handleRegenerateQuestion = async (question, index) => {
    const note = questionRegenerationNotes[question.id] || questionFeedback[question.id]?.note || '';
    const tags = questionFeedback[question.id]?.tags || [];
    const idempotencyBase = feedbackSessionId || `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    const attemptKey = `${idempotencyBase}:regen:${question.id}:${Date.now()}`;

    setRegeneratingQuestionIds((prev) => [...prev, question.id]);
    setError('');

    try {
      await submitFeedback(
        buildFeedbackContext(),
        {
          outcome: 'regenerated',
          regeneration_count: 1,
          teacher_edited: true,
          edit_description: tags.length
            ? `Regenerated single question due to ${tags.join(', ')}`
            : 'Regenerated single question with teacher instruction',
        },
        {
          tags: [...new Set([...tags, 'single_question_regenerated'])],
          teacher_note: note.trim().slice(0, 500),
          question_ref_id: String(question.id),
          idempotency_key: `answer-update:${attemptKey}:feedback`,
          question_characteristics: buildQuestionCharacteristics(question, tags),
          original_compact: question,
        }
      );

      const aiRes = await api.post(QUESTIONS_UPDATE_ANSWERS_WITH_AI, {
        question_ids: [question.id],
        update_scope: form.updateScope,
        note_to_ai: buildRegenerationInstruction(question, note, tags),
        feedback_context: buildFeedbackContext(),
      });
      const regenerated = aiRes.data?.results?.[0];
      if (!regenerated) {
        throw new Error('AI did not return an updated proposal for this question.');
      }

      const merged = mergeAIResultWithQuestion(regenerated, question);
      setUpdates((prev) => prev.map((item, itemIndex) => (
        itemIndex === index ? merged : item
      )));
      setQuestionRegenerationNotes((prev) => ({ ...prev, [question.id]: '' }));
    } catch (err) {
      console.error('Update with AI - regenerate one question failed', err);
      const msg = err?.message || getApiErrorMessage(err, 'Failed to regenerate this question');
      setError(msg);
      onError?.(msg);
    } finally {
      setRegeneratingQuestionIds((prev) => prev.filter((id) => id !== question.id));
    }
  };

  const handleSave = async () => {
    const toSave = selectedIndices.map((i) => updates[i]).filter(Boolean);
    if (!toSave.length) {
      setError('Please select at least one question to save.');
      return;
    }

    setLoading(true);
    setError('');
    let updatedCount = 0;

    try {
      for (const u of toSave) {
        const proposedQuestionText = (u.proposedQuestionText || '').trim();
        const correctTrimmed = (u.proposedCorrect || '').trim();
        const detailTrimmed = (u.proposedDetail || '').trim();

        if (
          form.updateScope === 'question'
          && proposedQuestionText
          && proposedQuestionText !== (u.existingQuestionText || '').trim()
        ) {
          await api.patch(`${QUESTIONS}${u.id}/`, {
            question_text: proposedQuestionText,
          });
          updatedCount += 1;
        }

        if (form.updateScope !== 'question' && correctTrimmed && correctTrimmed !== (u.existingCorrect || '').trim()) {
          await api.patch(`${QUESTIONS}${u.id}/`, {
            correct_answer: correctTrimmed,
            answer_evaluation: u.proposedAnswerEvaluation || {},
          });
          updatedCount += 1;
        }

        if (form.updateScope !== 'question' && detailTrimmed && detailTrimmed !== (u.existingDetail || '').trim()) {
          if (u.detailAnswerId) {
            await api.patch(`${DETAIL_ANSWERS}${u.detailAnswerId}/`, {
              answer: detailTrimmed,
            });
          } else {
            await api.post(DETAIL_ANSWERS, {
              question: u.id,
              answer: detailTrimmed,
            });
          }
          updatedCount += 1;
        }
      }

      const originalSelected = selectedIndices.map((i) => originalUpdates[i]).filter(Boolean);
      await submitFeedback(
        buildFeedbackContext(),
        buildImplicitSignals('accepted', originalSelected, toSave),
        {
          rating: feedbackRating,
          tags: feedbackTags,
          teacher_note: feedbackNote.trim().slice(0, 500),
          idempotency_key: feedbackSessionId ? `answer-update:${feedbackSessionId}:batch` : undefined,
          original_compact: originalSelected,
          edited_compact: toSave,
        }
      );

      const perQuestionFeedback = toSave
        .map((u) => {
          const feedback = questionFeedback[u.id] || { tags: [], note: '' };
          return {
            question: u,
            tags: feedback.tags || [],
            note: (feedback.note || '').trim().slice(0, 500),
          };
        })
        .filter((item) => item.tags.length || item.note);

      await Promise.all(perQuestionFeedback.map((item) => submitFeedback(
        buildFeedbackContext(),
        {
          outcome: 'edited',
          regeneration_count: 0,
          teacher_edited: true,
          edit_description: item.tags.length ? `Question feedback: ${item.tags.join(', ')}` : 'Question feedback note',
        },
        {
          tags: item.tags,
          teacher_note: item.note,
          question_ref_id: String(item.question.id),
          idempotency_key: feedbackSessionId ? `answer-update:${feedbackSessionId}:question:${item.question.id}` : undefined,
          question_characteristics: buildQuestionCharacteristics(item.question, item.tags),
        }
      )));
      onSuccess?.(updatedCount);
      onClose();
    } catch (err) {
      console.error('Update with AI – save error', err);
      const msg =
        err.response?.data?.error ||
        (form.updateScope === 'question'
          ? 'Failed to save AI-updated question text. Please review and try again.'
          : 'Failed to save AI-updated answers. Please review and try again.');
      setError(msg);
      onError?.(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleDialogClose = useCallback((event, reason) => {
    if (reason === 'backdropClick') {
      return;
    }
    onClose?.();
  }, [onClose]);

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth="lg"
      fullWidth
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          minHeight: fullScreen ? '100vh' : '60vh',
          minWidth: fullScreen ? '100%' : 760,
          width: fullScreen ? '100%' : 1040,
          maxWidth: fullScreen ? '100%' : 'calc(100vw - 32px)',
          maxHeight: fullScreen ? '100vh' : '90vh',
          borderRadius: fullScreen ? 0 : 2,
          overflow: 'auto',
          resize: fullScreen ? 'none' : 'both',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          pr: 1.5,
        }}
      >
        <AutoAwesomeIcon color="secondary" />
        <Box
          component="span"
          sx={{
            fontSize: { xs: '1rem', sm: '1.25rem' },
            fontWeight: 600,
            flexGrow: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          Update questions with AI
        </Box>
        <Button
          onClick={onClose}
          sx={{
            ml: 'auto',
            minWidth: 0,
            px: { xs: 1, sm: 1.5 },
            textTransform: 'none',
          }}
          startIcon={<CloseIcon />}
        >
          Close
        </Button>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 3 },
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            mb: 2,
            fontSize: { xs: '0.9rem', sm: '1rem' },
          }}
        >
          Select Board, Class (Year), Subject and Topic, then choose how many questions (5–10)
          to update. AI will propose the selected change, and you can accept, edit, or discard
          each proposal before saving.
        </Typography>

        <Stack spacing={2} sx={{ mb: 2 }}>
          <FormControl component="fieldset">
            <FormLabel component="legend">What should AI update?</FormLabel>
            <RadioGroup
              row
              value={form.updateScope}
              onChange={handleChange('updateScope')}
            >
              <FormControlLabel value="correct" control={<Radio />} label="Correct answer only" />
              <FormControlLabel value="detail" control={<Radio />} label="Detail answer only" />
              <FormControlLabel value="both" control={<Radio />} label="Both answers" />
              <FormControlLabel value="question" control={<Radio />} label="Question text" />
            </RadioGroup>
          </FormControl>
        </Stack>

        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            mb: 2,
          }}
        >
          <TextField
            select
            label="Board *"
            value={form.board}
            onChange={handleChange('board')}
            size="small"
            fullWidth
            sx={{ minWidth: { sm: 180 } }}
          >
            {boards.map((b) => (
              <MenuItem key={b.id} value={b.id}>
                {b.name || b.code}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Class / Year *"
            value={form.academic_year}
            onChange={handleChange('academic_year')}
            size="small"
            fullWidth
            sx={{ minWidth: { sm: 160 } }}
          >
            {academicYears.map((y) => (
              <MenuItem key={y.id} value={y.id}>
                {y.label || y.year}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Subject *"
            value={form.subject}
            onChange={(e) =>
              setForm((prev) => ({
                ...prev,
                subject: e.target.value,
                topic: '',
              }))
            }
            size="small"
            fullWidth
            sx={{ minWidth: { sm: 200 } }}
          >
            {subjects.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.name}
              </MenuItem>
            ))}
          </TextField>

          <Autocomplete
            size="small"
            options={availableTopics}
            getOptionLabel={(option) => {
              const name = option.topic || option.name || '';
              const count = option.question_count;
              return typeof count === 'number' ? `${name} (${count})` : name;
            }}
            value={
              availableTopics.find(
                (t) => String(t.id) === String(form.topic)
              ) || null
            }
            onChange={(e, newValue) =>
              setForm((prev) => ({
                ...prev,
                topic: newValue ? newValue.id : '',
              }))
            }
            isOptionEqualToValue={(option, value) =>
              option && value && option.id === value.id
            }
            disabled={!form.subject || topicsLoading}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Topic *"
                placeholder={topicsLoading ? 'Loading topics...' : 'Type to search topics...'}
                fullWidth
                sx={{ minWidth: { sm: 220 } }}
              />
            )}
            noOptionsText={form.board && form.academic_year && form.subject
              ? (form.updateScope === 'question' ? 'No topics with questions' : 'No topics with missing answers')
              : 'Select board, class and subject first'}
          />

          <TextField
            select
            label="Max questions *"
            value={form.maxQuestions}
            onChange={handleChange('maxQuestions')}
            size="small"
            fullWidth
            sx={{ minWidth: { sm: 160 } }}
          >
            {maxQuestionsOptions.map((n) => (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        <TextField
          label="Specific instruction for AI"
          value={form.promptNote}
          onChange={handleChange('promptNote')}
          multiline
          minRows={3}
          fullWidth
          placeholder={form.updateScope === 'question'
            ? 'Example: Rewrite in simpler class 5 language without changing the answer.'
            : 'Example: There is no X concept for this class. Use a concept that is applicable to this class instead.'}
          helperText={form.updateScope === 'question'
            ? 'Required. AI will rewrite the question text using this instruction.'
            : 'Add any class-specific guidance you want used to construct the AI prompt.'}
          required={form.updateScope === 'question'}
          sx={{ mb: 2 }}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 2 }}>
          <Button
            variant="contained"
            startIcon={<AutoAwesomeIcon />}
            onClick={handleGenerate}
            disabled={loading}
          >
            {form.updateScope === 'question' ? 'Find and propose text' : 'Find and propose answers'}
          </Button>
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && updates.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon color="success" fontSize="small" />
                Preview and edit before saving
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedIndices.length} of {updates.length} selected
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Edits you make below will be saved back to the question bank when you click Save updates.
            </Typography>
            {AI_FEEDBACK_UI_ENABLED && (
            <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 1.5, bgcolor: 'grey.50' }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                AI feedback <Typography component="span" variant="caption" color="text.secondary">(optional)</Typography>
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.25, flexWrap: 'wrap' }}>
                <Rating
                  value={feedbackRating}
                  onChange={(event, value) => setFeedbackRating(value)}
                />
                <Typography variant="body2" color="text.secondary">
                  {feedbackRating ? `${feedbackRating}/5` : `Rate the proposed ${form.updateScope === 'question' ? 'text' : 'answers'}`}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: feedbackRating && feedbackRating <= 3 ? 1.25 : 0 }}>
                {FEEDBACK_TAGS.map((tag) => (
                  <Chip
                    key={tag.value}
                    label={tag.label}
                    size="small"
                    color={feedbackTags.includes(tag.value) ? 'primary' : 'default'}
                    variant={feedbackTags.includes(tag.value) ? 'filled' : 'outlined'}
                    onClick={() => toggleFeedbackTag(tag.value)}
                  />
                ))}
              </Box>
              {feedbackRating && feedbackRating <= 3 && (
                <TextField
                  label="What should improve?"
                  value={feedbackNote}
                  onChange={(event) => setFeedbackNote(event.target.value.slice(0, 500))}
                  multiline
                  minRows={2}
                  fullWidth
                  size="small"
                  helperText={`${feedbackNote.length}/500`}
                />
              )}
            </Paper>
            )}
            <Stack spacing={1.5}>
              {updates.map((u, index) => (
                <Paper
                  key={u.id}
                  variant="outlined"
                  sx={{
                    p: 1.5,
                    borderRadius: 1.5,
                    borderColor: selectedIndices.includes(index) ? 'primary.main' : 'divider',
                    borderWidth: selectedIndices.includes(index) ? 2 : 1,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedIndices.includes(index)}
                          onChange={() => handleToggle(index)}
                          color="primary"
                        />
                      }
                      label={<Typography variant="subtitle2">Question {index + 1}</Typography>}
                      sx={{ mr: 0 }}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Q{u.id}
                  </Typography>
                  {form.updateScope === 'question' ? (
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                        gap: 1.5,
                        mb: 1.5,
                      }}
                    >
                      <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                          Original question
                        </Typography>
                        <MathRenderer
                          content={u.existingQuestionText || u.question_text}
                          variant="body2"
                          sx={{ fontWeight: 500, lineHeight: 1.5 }}
                        />
                      </Paper>
                      <MathEditor
                        value={u.proposedQuestionText ?? ''}
                        onChange={(value) => handleUpdateField(u.id, 'proposedQuestionText', value)}
                        label="Updated question"
                        placeholder="Review or edit the proposed question text"
                        helperText="Editable before saving. Existing answer and options should remain valid."
                        minRows={3}
                        fullWidth
                        showPreview={true}
                      />
                    </Box>
                  ) : (
                    <Box sx={{ mb: 1 }}>
                      <MathRenderer
                        content={u.question_text}
                        variant="body2"
                        sx={{ fontWeight: 500, lineHeight: 1.5 }}
                      />
                    </Box>
                  )}
                  {(u.image_proxy_url || u.image_url) && (
                    <Box sx={{ mb: 1.5 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                        Question image
                      </Typography>
                      <QuestionImage question={u} alt={`Question ${index + 1} image`} />
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {(form.updateScope === 'both' || form.updateScope === 'correct') && (
                      u.question_type === 'MULTIPLE' && Array.isArray(u.options) && u.options.length > 0 ? (
                        (() => {
                          const opts = u.options.map((o) => String(o ?? '').trim()).filter(Boolean);
                          const current = typeof u.proposedCorrect === 'string' ? u.proposedCorrect.trim() : (u.proposedCorrect ?? '');
                          const isNotAnOption = Boolean(current) && !opts.includes(current);
                          return (
                            <TextField
                              select
                              label="Correct answer"
                              size="small"
                              fullWidth
                              value={current}
                              onChange={(e) => handleUpdateField(u.id, 'proposedCorrect', e.target.value)}
                              error={isNotAnOption}
                              helperText={
                                isNotAnOption
                                  ? `"${current}" is not one of the options — select a valid option`
                                  : 'Select the option that is the correct answer'
                              }
                            >
                              <MenuItem value="">—</MenuItem>
                              {isNotAnOption && (
                                <MenuItem value={current}>{current} (not in options)</MenuItem>
                              )}
                              {opts.map((opt, i) => (
                                <MenuItem key={`${opt}-${i}`} value={opt}>{opt}</MenuItem>
                              ))}
                            </TextField>
                          );
                        })()
                      ) : (
                        <MathEditor
                          value={u.proposedCorrect ?? ''}
                          onChange={(val) =>
                            handleUpdateField(u.id, 'proposedCorrect', val)
                          }
                          label="Correct answer"
                          placeholder="Enter or edit the correct answer"
                          helperText="Use the math button (∑) for formulas."
                          minRows={1}
                          fullWidth
                          showPreview={true}
                        />
                      )
                    )}
                    {(form.updateScope === 'both' || form.updateScope === 'detail') && (
                    <MathEditor
                      value={u.proposedDetail ?? ''}
                      onChange={(val) =>
                        handleUpdateField(u.id, 'proposedDetail', val)
                      }
                      label="Detail answer (worked solution)"
                      placeholder="Step-by-step solution..."
                      helperText="Use the math button (∑) to insert mathematical formulas."
                      minRows={3}
                      fullWidth
                    />
                    )}
                  </Box>
                  {form.updateScope === 'question' && (
                    <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                      <Button
                        size="small"
                        variant={selectedIndices.includes(index) ? 'contained' : 'outlined'}
                        onClick={() => setProposalAccepted(index, true)}
                      >
                        Accept
                      </Button>
                      <Button
                        size="small"
                        color="inherit"
                        variant={!selectedIndices.includes(index) ? 'contained' : 'outlined'}
                        onClick={() => setProposalAccepted(index, false)}
                      >
                        Discard
                      </Button>
                    </Box>
                  )}
                  {AI_FEEDBACK_UI_ENABLED && (
                    <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                        Issue with this AI update? <Typography component="span" variant="caption" color="text.secondary">(optional)</Typography>
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1 }}>
                        {QUESTION_FEEDBACK_TAGS.map((tag) => {
                          const selected = (questionFeedback[u.id]?.tags || []).includes(tag.value);
                          return (
                            <Chip
                              key={tag.value}
                              label={tag.label}
                              size="small"
                              color={selected ? 'primary' : 'default'}
                              variant={selected ? 'filled' : 'outlined'}
                              onClick={() => toggleQuestionFeedbackTag(u.id, tag.value)}
                            />
                          );
                        })}
                      </Box>
                      {(questionFeedback[u.id]?.tags || []).length > 0 && (
                        <TextField
                          label="Optional note for this question"
                          value={questionFeedback[u.id]?.note || ''}
                          onChange={(event) => updateQuestionFeedbackNote(u.id, event.target.value)}
                          size="small"
                          fullWidth
                          multiline
                          minRows={1}
                          helperText={`${(questionFeedback[u.id]?.note || '').length}/500`}
                        />
                      )}
                      <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'flex-start', flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
                        <TextField
                          label="Instruction for regenerating this question"
                          value={questionRegenerationNotes[u.id] || ''}
                          onChange={(event) => updateQuestionRegenerationNote(u.id, event.target.value)}
                          size="small"
                          fullWidth
                          placeholder="Example: make the steps shorter and use class 5 language"
                          helperText={`${(questionRegenerationNotes[u.id] || '').length}/500`}
                        />
                        <Button
                          variant="outlined"
                          startIcon={
                            regeneratingQuestionIds.includes(u.id)
                              ? <CircularProgress size={16} />
                              : <ReplayIcon />
                          }
                          onClick={() => handleRegenerateQuestion(u, index)}
                          disabled={loading || regeneratingQuestionIds.includes(u.id)}
                          sx={{ minWidth: 150, mt: { xs: 0, md: 0.25 } }}
                        >
                          Regenerate
                        </Button>
                      </Box>
                    </Box>
                  )}
                </Paper>
              ))}
            </Stack>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading || updates.length === 0 || selectedIndices.length === 0}
        >
          {updates.length > 0
            ? `Save Selected (${selectedIndices.length})`
            : 'Save updates'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
